import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ClipboardList, 
  FileText, 
  HardHat, 
  Users, 
  Package, 
  Calendar, 
  AlertTriangle,
  ArrowRight,
  Clock,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { orEmptyIfForbidden, getDashboardStats, getServiceRequests, getProjects } from '../../services/api';

export const DashboardPage = () => {
  const { user, can } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentServices, setRecentServices] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, servicesRes, projectsRes] = await Promise.all([
        getDashboardStats(),
        orEmptyIfForbidden(getServiceRequests()),
        orEmptyIfForbidden(getProjects())
      ]);
      setStats(statsRes.data);
      setRecentServices(servicesRes.data.slice(0, 5));
      setRecentProjects(projectsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const adminStats = [
    { 
      label: 'Appels en attente', 
      value: stats?.pending_services || 0, 
      icon: ClipboardList,
      color: 'text-orange-600 bg-orange-100',
      link: '/dashboard/service-requests'
    },
    { 
      label: 'Soumissions', 
      value: stats?.pending_quotes || 0, 
      icon: FileText,
      color: 'text-blue-600 bg-blue-100',
      link: '/dashboard/quotes'
    },
    { 
      label: 'Chantiers actifs', 
      value: stats?.active_projects || 0, 
      icon: HardHat,
      color: 'text-purple-600 bg-purple-100',
      link: '/dashboard/projects'
    },
    { 
      label: 'Employés', 
      value: stats?.total_employees || 0, 
      icon: Users,
      color: 'text-green-600 bg-green-100',
      link: '/dashboard/employees'
    },
    { 
      label: 'Congés en attente', 
      value: stats?.pending_leaves || 0, 
      icon: Calendar,
      color: 'text-cyan-600 bg-cyan-100',
      link: '/dashboard/leave-requests'
    },
    { 
      label: 'Stock faible', 
      value: stats?.low_stock_items || 0, 
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      link: '/dashboard/inventory'
    },
  ];

  const employeeStats = [
    { 
      label: 'Mes chantiers', 
      value: stats?.my_projects || 0, 
      icon: HardHat,
      color: 'text-purple-600 bg-purple-100',
      link: '/dashboard/projects'
    },
    { 
      label: 'Mes appels', 
      value: stats?.my_services || 0, 
      icon: ClipboardList,
      color: 'text-orange-600 bg-orange-100',
      link: '/dashboard/service-requests'
    },
    { 
      label: 'Entrées aujourd\'hui', 
      value: stats?.today_entries || 0, 
      icon: Clock,
      color: 'text-blue-600 bg-blue-100',
      link: '/dashboard/time-entries'
    },
    { 
      label: 'Congés en attente', 
      value: stats?.pending_leaves || 0, 
      icon: Calendar,
      color: 'text-cyan-600 bg-cyan-100',
      link: '/dashboard/leave-requests'
    },
  ];

  // Le serveur renvoie null pour un indicateur non autorisé.
  const TEAM_KEYS = { 'Appels en attente': 'pending_services', 'Soumissions': 'pending_quotes', 'Chantiers actifs': 'active_projects', 'Employés': 'total_employees', 'Congés en attente': 'pending_leaves', 'Stock faible': 'low_stock_items' };
  const teamView = ['pending_services', 'pending_quotes', 'active_projects', 'total_employees']
    .some((key) => stats?.[key] !== null && stats?.[key] !== undefined);
  const currentStats = teamView
    ? adminStats.filter((card) => stats?.[TEAM_KEYS[card.label]] !== null && stats?.[TEAM_KEYS[card.label]] !== undefined)
    : employeeStats;
  const quickActions = {
    project: can('projects.create'),
    employee: can('employees.create'),
    inventory: can('inventory.manage'),
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      confirmed: 'badge-confirmed',
      assigned: 'badge-confirmed',
      in_progress: 'badge-in-progress',
      completed: 'badge-completed',
      cancelled: 'badge-cancelled'
    };
    return badges[status] || 'badge-pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      assigned: 'Assigné',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1a3a5c]">
          Bonjour, {user?.first_name}!
        </h1>
        <p className="text-slate-500 mt-1">
          Voici un aperçu de votre activité
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {currentStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className="card-hover cursor-pointer h-full" data-testid={`stat-card-${index}`}>
                <CardContent className="p-4">
                  <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-2xl font-bold text-[#1a3a5c]">{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Services */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#1a3a5c]">
              Appels de service récents
            </CardTitle>
            <Link to="/dashboard/service-requests">
              <Button variant="ghost" size="sm" className="text-[#00b4d8]">
                Voir tout
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentServices.length > 0 ? (
              <div className="space-y-4">
                {recentServices.map((service) => (
                  <div 
                    key={service.id} 
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1a3a5c] truncate">
                        {service.first_name} {service.last_name}
                      </p>
                      <p className="text-sm text-slate-500 truncate">
                        {service.address}
                      </p>
                    </div>
                    <div className="ml-4 flex items-center gap-3">
                      <span className="text-sm text-slate-500">
                        {service.appointment_date}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(service.status)}`}>
                        {getStatusLabel(service.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun appel de service</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Projects */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#1a3a5c]">
              Chantiers récents
            </CardTitle>
            <Link to="/dashboard/projects">
              <Button variant="ghost" size="sm" className="text-[#00b4d8]">
                Voir tout
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentProjects.length > 0 ? (
              <div className="space-y-4">
                {recentProjects.map((project) => (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1a3a5c] truncate">
                        {project.client_name}
                      </p>
                      <p className="text-sm text-slate-500 truncate">
                        {project.description}
                      </p>
                    </div>
                    <div className="ml-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(project.status)}`}>
                        {getStatusLabel(project.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <HardHat className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun chantier</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {(quickActions.project || quickActions.employee || quickActions.inventory) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1a3a5c]">
              Actions rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {quickActions.project && <Link to="/dashboard/projects">
                <Button className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]" data-testid="quick-add-project">
                  <HardHat className="mr-2 w-4 h-4" />
                  Nouveau chantier
                </Button>
              </Link>}
              {quickActions.employee && <Link to="/dashboard/employees">
                <Button variant="outline" className="rounded-full" data-testid="quick-add-employee">
                  <Users className="mr-2 w-4 h-4" />
                  Ajouter employé
                </Button>
              </Link>}
              {quickActions.inventory && <Link to="/dashboard/inventory">
                <Button variant="outline" className="rounded-full" data-testid="quick-add-inventory">
                  <Package className="mr-2 w-4 h-4" />
                  Ajouter inventaire
                </Button>
              </Link>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
